import React, { useEffect, useState } from "react";
import {
    SmallActionButton,
    PlaylistThumb,
    StandardModal,
    StandardModalActionButton,
    StandardModalActionGroup,
    StandardModalSection,
    StandardModalSectionTitle,
    StandardModalTable,
    StandardModalTableScroll,
    StandardModalTableSection,
    portalColors
} from "src/components";
import {
    PlatformPill,
    ToggleTrack,
    getVisibilityText
} from "./shared";
import styled from "styled-components";
import SpotifyLogo from "src/img/spotify@2x.png";
import AppleMusicLogo from "src/img/apple music@2x.png";

const VisibilityBadge = styled.div`
    font-weight: 600;
    color: ${portalColors.text};
`;

export default function ManagePlaylistsModal({
    playlists,
    onClose,
    onCreate,
    onEdit,
    onSave
}) {
    const [activeMap, setActiveMap] = useState(() => {
        const initialMap = {};
        playlists.forEach(playlist => {
            initialMap[playlist.uuid] = Boolean(playlist.isPlaylistVisible);
        });
        return initialMap;
    });
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        const nextMap = {};
        playlists.forEach(playlist => {
            nextMap[playlist.uuid] = Boolean(playlist.isPlaylistVisible);
        });
        setActiveMap(nextMap);
    }, [playlists]);

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const activeIds = playlists.filter(playlist => activeMap[playlist.uuid]).map(playlist => playlist.uuid);
            await onSave(activeIds);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <StandardModal
            open={true}
            onClose={onClose}
            widthMode="wide"
            title="Manage Playlists"
            description="Select the toggle to hide or show playlists to your user base."
            headerAside={(
                <StandardModalActionButton
                    type="button"
                    $variant="primary"
                    width="140px"
                    onClick={onCreate}
                >
                    Create Playlist
                </StandardModalActionButton>
            )}
            footer={(
                <StandardModalActionGroup $pushRight>
                    <StandardModalActionButton
                        type="button"
                        width="8.75rem"
                        onClick={onClose}
                    >
                        Close
                    </StandardModalActionButton>
                    <StandardModalActionButton
                        type="button"
                        $variant="primary"
                        width="8.75rem"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? "Saving..." : "Save"}
                    </StandardModalActionButton>
                </StandardModalActionGroup>
            )}
        >
            <StandardModalSection>
                <StandardModalSectionTitle>Available Playlists</StandardModalSectionTitle>
                <StandardModalTableSection>
                    <StandardModalTableScroll maxHeight="min(32rem, calc(100vh - 26rem))">
                        <StandardModalTable minWidth="760px">
                            <thead>
                                <tr>
                                    <th>Playlist Image</th>
                                    <th>Playlist Title</th>
                                    <th>Availability</th>
                                    <th>Visibility</th>
                                    <th>View/Edit</th>
                                    <th style={{ textAlign: "center" }}>Show/Hide</th>
                                </tr>
                            </thead>
                            <tbody>
                                {playlists.map(playlist => (
                                    <tr key={playlist.uuid}>
                                        <td>
                                            <PlaylistThumb size="46px">
                                                {playlist.imageUrl && <img src={playlist.imageUrl} alt={playlist.name} />}
                                            </PlaylistThumb>
                                        </td>
                                        <td style={{ fontWeight: 600 }}>{playlist.name}</td>
                                        <td>
                                            <div style={{ display: "flex", gap: 10 }}>
                                                { playlist.spotifyUri &&
                                                <PlatformPill src={SpotifyLogo} />
                                                }
                                                { playlist.appleMusicUrl &&
                                                <PlatformPill src={AppleMusicLogo} />
                                                }
                                            </div>
                                        </td>
                                        <td><VisibilityBadge>{getVisibilityText(playlist)}</VisibilityBadge></td>
                                        <td>
                                            {playlist.isCompanyList ? (
                                                <SmallActionButton type="button" onClick={() => onEdit(playlist)}>View/Edit</SmallActionButton>
                                            ) : (
                                                <div style={{ fontSize: 11, fontWeight: 600, color: "#2d3444", maxWidth: 96 }}>Me Business Playlist</div>
                                            )}
                                        </td>
                                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                            <ToggleTrack
                                                type="button"
                                                checked={activeMap[playlist.uuid]}
                                                onClick={() => setActiveMap(prev => ({ ...prev, [playlist.uuid]: !prev[playlist.uuid] }))}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </StandardModalTable>
                    </StandardModalTableScroll>
                </StandardModalTableSection>
            </StandardModalSection>
        </StandardModal>
    );
}
